import {useState, useEffect, useRef} from '@wordpress/element';
import {useCheckout} from '@stripe/react-stripe-js/checkout';
import {
    handleNextAction,
    isNextActionRequired,
    ensureSuccessResponse,
    ensureErrorResponse
} from '@wc-stripe/blocks-utils';

/**
 * Drives payment for a Blocks payment method backed by a Stripe CheckoutSession. Unlike the
 * deferred-intent model's useCreatePaymentMethod, there's nothing to create client-side ahead of
 * order placement - checkout.confirm() does everything, once the server has linked the session to
 * the order and returned the #response= redirectUrl onCheckoutSuccess receives.
 *
 * useCheckout(), not useCheckoutElements() - the latter needs @stripe/react-stripe-js v6+
 * (installed here: 5.6.1), which docs note requires useCheckout() instead.
 *
 * @param {string}   name
 * @param {string}   gatewayId
 * @param {Object}   emitResponse
 * @param {Object}   billingAddress
 * @param {Object}   eventRegistration
 * @param {boolean}  shouldSavePayment
 * @param {string}   activePaymentMethod
 */
export const useCheckoutSessionPayment = (
    {
        name,
        gatewayId,
        emitResponse,
        billingAddress,
        eventRegistration,
        shouldSavePayment,
        activePaymentMethod
    }) => {
    const {onPaymentSetup, onCheckoutSuccess} = eventRegistration;
    const checkoutState = useCheckout();
    const [paymentMethodType, setPaymentMethodType] = useState('');

    const currentData = useRef({
        emitResponse,
        billingAddress,
        checkoutState,
        shouldSavePayment,
        paymentMethodType,
        activePaymentMethod
    });
    useEffect(() => {
        currentData.current = {
            emitResponse,
            billingAddress,
            checkoutState,
            shouldSavePayment,
            paymentMethodType,
            activePaymentMethod
        };
    }, [emitResponse, billingAddress, checkoutState, shouldSavePayment, paymentMethodType, activePaymentMethod]);

    // Validates the Payment Element before the order is placed - there's no payment method to
    // create here, actual confirmation happens in onCheckoutSuccess below.
    useEffect(() => {
        const unsubscribe = onPaymentSetup(async () => {
            const {activePaymentMethod, emitResponse, checkoutState, shouldSavePayment, paymentMethodType} = currentData.current;
            if (activePaymentMethod !== name) {
                return null;
            }
            try {
                if (checkoutState.type !== 'success') {
                    throw checkoutState.type === 'error'
                        ? checkoutState.error
                        : new Error('Checkout session is not ready.');
                }
                const response = await checkoutState.checkout.validateElements();
                if (response.error) {
                    throw response.error;
                }
                return ensureSuccessResponse(emitResponse.responseTypes, {
                    meta: {
                        paymentMethodData: {
                            [`wc-${gatewayId}-new-payment-method`]: shouldSavePayment,
                            _stripe_payment_method_type: response.selectedPaymentMethod || paymentMethodType
                        }
                    }
                });
            } catch (error) {
                return ensureErrorResponse(
                    emitResponse.responseTypes,
                    error,
                    {messageContext: emitResponse.noticeContexts.PAYMENTS}
                );
            }
        });
        return () => unsubscribe();
    }, [name, gatewayId, onPaymentSetup]);

    // The order now exists and the session is linked to it - confirm the session and let
    // handleNextAction's checkout_session branch redirect to complete_payment_return_url.
    useEffect(() => {
        const unsubscribe = onCheckoutSuccess(async ({redirectUrl}) => {
            const {activePaymentMethod} = currentData.current;
            if (activePaymentMethod !== name) {
                return;
            }
            const args = isNextActionRequired(redirectUrl);
            if (!args) {
                return;
            }
            const {emitResponse, billingAddress, checkoutState} = currentData.current;
            try {
                const result = await handleNextAction({
                    args,
                    checkout: checkoutState.checkout,
                    billingAddress
                });
                return ensureSuccessResponse(emitResponse.responseTypes, result);
            } catch (error) {
                return ensureErrorResponse(
                    emitResponse.responseTypes,
                    error,
                    {messageContext: emitResponse.noticeContexts.PAYMENTS}
                );
            }
        });
        return () => unsubscribe();
    }, [name, onCheckoutSuccess]);

    return {setPaymentMethodType};
};